import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import { cx } from '@/lib/cx';
import type { FlightStatus } from '@/types/flight';
import { FLIGHT_STATUSES } from '@/types/flight';
import { TINT } from './statusTint';
import styles from './SchedulePage.module.css';

interface StatusFilterProps {
  /** The statuses in force — what is in the URL. */
  values: readonly FlightStatus[];
  onChange: (values: FlightStatus[]) => void;
  ariaLabel: string;
}

/** Where to paint the menu, in viewport coordinates. */
interface Box {
  top: number;
  left: number;
  width: number;
}

/**
 * The status column: any of the statuses, ticked in a list.
 *
 * Native checkboxes. Thirteen fixed options need no search, and a real checkbox
 * brings Space, its focus and its announced state with it. Each carries the tint its
 * status paints the rows with, so the list is also the key to the board's colours.
 *
 * A tick applies at once, as every other column filter does, and the list stays open
 * for the next. The trigger says what is in force: the one status, or the first and
 * how many more — the whole list is on its title.
 *
 * Portalled and placed from the trigger's rect for CodeFilter's reason: the table
 * scrolls sideways, and its overflow would clip a menu left inside the header.
 */
export const StatusFilter = ({ values, onChange, ariaLabel }: StatusFilterProps) => {
  const { t } = useTranslation();
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<Box | null>(null);
  const isOpen = box !== null;

  const measure = (): Box | null => {
    const rect = triggerRef.current?.getBoundingClientRect();
    return rect ? { top: rect.bottom + 4, left: rect.left, width: rect.width } : null;
  };

  /** Closes, and hands focus back to the trigger when the keyboard closed it. */
  const close = (refocus: boolean) => {
    setBox(null);
    if (refocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!isOpen) return;

    const reposition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setBox({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setBox(null);
    };

    window.addEventListener('scroll', reposition, { capture: true, passive: true });
    window.addEventListener('resize', reposition);
    document.addEventListener('pointerdown', onPointerDown);

    // Into the list on opening — onto the first ticked status, or else the first — so
    // the keyboard is where the choice is.
    const menu = menuRef.current;
    (menu?.querySelector<HTMLInputElement>('input:checked') ?? menu?.querySelector<HTMLInputElement>('input'))?.focus();

    return () => {
      window.removeEventListener('scroll', reposition, { capture: true });
      window.removeEventListener('resize', reposition);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isOpen]);

  /** The new set, kept in the canonical order so the URL does not depend on click order. */
  const toggle = (status: FlightStatus) =>
    onChange(FLIGHT_STATUSES.filter((each) => (each === status) !== values.includes(each)));

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const boxes = [...(menuRef.current?.querySelectorAll<HTMLInputElement>('input') ?? [])];
    const at = boxes.indexOf(document.activeElement as HTMLInputElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }

    // Arrows move as they do in any list; checkboxes alone do not.
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      boxes[Math.min(Math.max(at + step, 0), boxes.length - 1)]?.focus();
      return;
    }

    // Tabbing off either end closes and returns to the trigger. The menu is at the
    // end of <body>, so the browser's next stop after it is nowhere near the table.
    if (event.key === 'Tab') {
      const atEdge = event.shiftKey ? at === 0 : at === boxes.length - 1;
      if (atEdge) {
        event.preventDefault();
        close(true);
      }
    }
  };

  const [first] = values;
  const nameOf = (status: FlightStatus) => t(`schedule.statuses.${status}`);
  const summary =
    first === undefined ? '—' : values.length === 1 ? nameOf(first) : `${nameOf(first)} +${values.length - 1}`;

  return (
    <span className={styles.filterCell}>
      <button
        type="button"
        ref={triggerRef}
        className={cx(styles.filterInput, styles.statusTrigger, values.length > 0 && styles.filterActive)}
        onClick={() => (isOpen ? close(false) : setBox(measure()))}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !isOpen) {
            event.preventDefault();
            setBox(measure());
          }
        }}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-label={`${ariaLabel}: ${values.length === 0 ? '—' : values.map(nameOf).join(', ')}`}
        title={values.length > 1 ? values.map(nameOf).join(', ') : undefined}
      >
        <span className={styles.statusSummary}>{summary}</span>
      </button>

      {/* The clear where a filter's clear always is; the chevron while there is
          nothing to clear, so an empty filter still reads as a dropdown. */}
      {values.length > 0 ? (
        <button
          type="button"
          className={styles.filterClear}
          onClick={() => onChange([])}
          aria-label={t('schedule.clearFilter', { name: ariaLabel })}
        >
          ×
        </button>
      ) : (
        <ChevronDownIcon className={styles.statusChevron} />
      )}

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="group"
            aria-label={ariaLabel}
            className={cx(styles.comboMenu, styles.statusMenu)}
            style={{ top: box.top, left: box.left, minWidth: Math.max(box.width, 220) }}
            onKeyDown={handleMenuKeyDown}
          >
            {FLIGHT_STATUSES.map((status) => (
              <label key={status} className={cx(styles.statusOption, TINT[status])}>
                <input
                  type="checkbox"
                  className={styles.statusCheck}
                  checked={values.includes(status)}
                  onChange={() => toggle(status)}
                />
                <span className={styles.legendSwatch} aria-hidden="true" />
                {nameOf(status)}
              </label>
            ))}
          </div>,
          document.body,
        )}
    </span>
  );
};
